/**
 * VatRateCard — FASE 0 IVA
 * Card riusabile per gestione aliquota IVA su Contract o Project (fallback).
 *
 * Props:
 * - vatRate: number (attuale)
 * - contractAmount: number (netto)
 * - onSave: async (newRate: number) => void
 * - readOnly: bool (true = mostra solo, non modificabile — es. project con contract collegato)
 * - canEdit: bool (default true) — se false disabilita input + Salva anche se non readOnly.
 *   Usato per limitare modifica ai soli ruoli admin/administration.
 * - needsReview: bool (mostra badge "Da rivedere")
 * - reviewReasons: string[]
 * - source: 'contract' | 'project' (per label)
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group';
import { AlertTriangle, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { formatCurrency } from '../../lib/utils';

const PRESETS = [4, 10, 22];
const MIN_VAT = 0;
const MAX_VAT = 25;

export const VatRateCard = ({
  vatRate = 10,
  contractAmount = 0,
  onSave,
  readOnly = false,
  canEdit = true,
  needsReview = false,
  reviewReasons = [],
  source = 'contract',
  viewModeFirst = false,
  title = 'Aliquota IVA lavori',
}) => {
  const initialIsPreset = PRESETS.includes(Number(vatRate));
  const [selectedPreset, setSelectedPreset] = useState(initialIsPreset ? String(vatRate) : 'altro');
  const [customValue, setCustomValue] = useState(initialIsPreset ? '' : String(vatRate));
  const [saving, setSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);

  // Modalita' effettiva di sola lettura:
  //  - readOnly esplicito (progetto con contract collegato)
  //  - oppure canEdit=false (utente senza permessi admin/administration)
  const isReadOnly = readOnly || !canEdit;
  const lockedByPermission = !readOnly && !canEdit;
  // In modalita' viewModeFirst mostriamo la view di lettura finche' l'utente non preme Modifica
  const showForm = !isReadOnly && (viewModeFirst ? isEditing : true);

  const RATE_DESCRIPTIONS = {
    4: 'Prima casa',
    10: 'Ristrutturazione/manutenzione',
    22: 'Nuova costruzione',
  };

  // Sync quando cambia la prop
  useEffect(() => {
    const isPreset = PRESETS.includes(Number(vatRate));
    setSelectedPreset(isPreset ? String(vatRate) : 'altro');
    setCustomValue(isPreset ? '' : String(vatRate));
  }, [vatRate]);

  const currentRate = selectedPreset === 'altro'
    ? parseFloat(customValue) || 0
    : parseFloat(selectedPreset);

  const vatAmount = (contractAmount || 0) * (currentRate / 100);
  const grossAmount = (contractAmount || 0) + vatAmount;

  const isDirty = Number(currentRate) !== Number(vatRate);

  const handleCancelEdit = () => {
    const isPreset = PRESETS.includes(Number(vatRate));
    setSelectedPreset(isPreset ? String(vatRate) : 'altro');
    setCustomValue(isPreset ? '' : String(vatRate));
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (currentRate < MIN_VAT || currentRate > MAX_VAT) {
      toast.error(`Aliquota IVA deve essere tra ${MIN_VAT}% e ${MAX_VAT}%`);
      return;
    }
    setSaving(true);
    try {
      await onSave(currentRate);
      toast.success(`Aliquota IVA aggiornata a ${currentRate}%`);
      setIsEditing(false);
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Errore aggiornamento IVA');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="card-abitalo h-full" data-testid={`vat-rate-card-${source}`}>
      <CardHeader className="pb-2">
        <CardTitle className="text-base flex items-center gap-2">
          <Receipt className="h-4 w-4 text-[#FF7819]" />
          {title}
          {readOnly && (
            <Badge variant="outline" className="ml-auto text-xs" data-testid="vat-badge-from-contract">
              Da Contratto
            </Badge>
          )}
          {lockedByPermission && (
            <Badge variant="outline" className="ml-auto text-xs" data-testid="vat-badge-locked">
              Solo amministrazione
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {needsReview && (
          <div
            className="flex items-start gap-2 p-2 rounded-md bg-amber-50 border border-amber-200 text-amber-800"
            data-testid="vat-review-warning"
          >
            <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
            <div className="text-xs">
              <div className="font-medium">Da rivedere</div>
              {reviewReasons?.length > 0 && (
                <ul className="mt-1 list-disc list-inside">
                  {reviewReasons.slice(0, 3).map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {showForm ? (
          <RadioGroup
            value={selectedPreset}
            onValueChange={setSelectedPreset}
            className="space-y-2"
          >
            {PRESETS.map((p) => (
              <div key={p} className="flex items-center space-x-2">
                <RadioGroupItem value={String(p)} id={`vat-${p}-${source}`} data-testid={`vat-preset-${p}`} />
                <Label htmlFor={`vat-${p}-${source}`} className="cursor-pointer text-sm">
                  {p}% {p === 4 ? '(prima casa)' : p === 10 ? '(ristrutturazione/manutenzione)' : p === 22 ? '(nuova costruzione)' : ''}
                </Label>
              </div>
            ))}
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="altro" id={`vat-altro-${source}`} data-testid="vat-preset-altro" />
              <Label htmlFor={`vat-altro-${source}`} className="cursor-pointer text-sm">
                Altro:
              </Label>
              <Input
                type="number"
                min={MIN_VAT}
                max={MAX_VAT}
                step={0.5}
                value={customValue}
                onChange={(e) => {
                  setCustomValue(e.target.value);
                  setSelectedPreset('altro');
                }}
                disabled={selectedPreset !== 'altro'}
                className="w-24 h-8"
                placeholder="0-25"
                data-testid="vat-custom-input"
              />
              <span className="text-sm text-[#737373]">%</span>
            </div>
          </RadioGroup>
        ) : (
          <div data-testid="vat-view-mode">
            <p className="text-sm font-medium text-[#525252]">IVA lavori</p>
            <p className="text-2xl font-semibold text-[#1F1F1F] leading-tight" data-testid="vat-rate-readonly-value">
              {vatRate}%
              {RATE_DESCRIPTIONS[Number(vatRate)] && (
                <span className="text-sm font-normal text-[#737373]"> · {RATE_DESCRIPTIONS[Number(vatRate)]}</span>
              )}
            </p>
            {lockedByPermission && (
              <p className="text-xs text-[#737373] mt-1" data-testid="vat-locked-hint">
                Modifica riservata ad amministrazione
              </p>
            )}
          </div>
        )}

        <div className="pt-2 border-t space-y-1 text-sm" data-testid="vat-breakdown">
          <div className="flex justify-between text-[#525252]">
            <span>Imponibile</span>
            <span className="tabular-nums">{formatCurrency(contractAmount)}</span>
          </div>
          <div className="flex justify-between text-[#525252]">
            <span>IVA ({currentRate}%)</span>
            <span className="tabular-nums">{formatCurrency(vatAmount)}</span>
          </div>
          <div className="flex justify-between font-semibold text-[#1F1F1F] pt-1">
            <span>Totale IVA inclusa</span>
            <span className="tabular-nums">{formatCurrency(grossAmount)}</span>
          </div>
        </div>

        {showForm ? (
          <div className="flex gap-2">
            <Button
              onClick={handleSave}
              disabled={!isDirty || saving}
              className="flex-1 bg-[#FF7819] hover:bg-[#E56A10]"
              data-testid="vat-save-btn"
            >
              {saving ? 'Salvataggio...' : 'Salva aliquota'}
            </Button>
            {viewModeFirst && (
              <Button variant="outline" onClick={handleCancelEdit} disabled={saving} data-testid="vat-cancel-btn">
                Annulla
              </Button>
            )}
          </div>
        ) : (
          !isReadOnly && viewModeFirst && (
            <Button variant="outline" onClick={() => setIsEditing(true)} className="w-full" data-testid="vat-edit-btn">
              Modifica
            </Button>
          )
        )}
      </CardContent>
    </Card>
  );
};

export default VatRateCard;
